export type Service = {
  id: string;
  name: string;
  /** Short label for the jump bar on the Services page. */
  navLabel: string;
  /** One-liner used on the Home page list. */
  short: string;
  /** Longer description used on the Services page. */
  long: string;
  deliverables: string[];
  /** Small "BUILT WITH" line on the Services page. */
  toolsLabel: string;
  tools: string[];
};

export const SERVICES: Service[] = [
  {
    id: "web",
    navLabel: "Web",
    name: "Web Development",
    short: "Marketing sites, web apps and online stores — fast, secure and built to grow with you.",
    long: "Marketing sites, web apps and e-commerce built on modern frameworks. Fast to load, easy to update, and engineered to scale when your traffic does.",
    deliverables: ["Websites", "Web apps", "E-commerce"],
    toolsLabel: "Built with",
    tools: ["React", "Angular", "Vue", "Node.js", "Shopify"],
  },
  {
    id: "mobile",
    navLabel: "Mobile",
    name: "Mobile Apps",
    short: "iOS and Android apps, native or cross-platform, that customers keep on their home screen.",
    long: "iOS and Android apps — native, or cross-platform with React Native and Flutter. From first prototype to app store release, and every update after.",
    deliverables: ["Android", "iOS", "Cross-platform"],
    toolsLabel: "Built with",
    tools: ["React Native", "Flutter", "Fastlane"],
  },
  {
    id: "cloud",
    navLabel: "Cloud",
    name: "Cloud & DevOps",
    short: "Migrations, pipelines and automated infrastructure so releases are routine and downtime is rare.",
    long: "Move to the cloud, or get more out of it. We set up secure infrastructure, automated pipelines and monitoring so shipping is routine and scaling is a setting, not a project.",
    deliverables: ["Migration", "Security", "CI/CD", "Deployment", "Automation"],
    toolsLabel: "Built with",
    tools: ["AWS", "Google Cloud", "Microsoft Azure"],
  },
  {
    id: "uxui",
    navLabel: "UX/UI",
    name: "UX/UI Design",
    short: "Research-led product design that makes complex software feel simple.",
    long: "Products people understand on first use. Research, flows and prototypes come first — then a polished interface your engineers can build exactly as designed.",
    deliverables: ["Research", "Wireframes", "Prototypes", "UI design"],
    toolsLabel: "For",
    tools: ["Web", "Mobile", "Responsive"],
  },
  {
    id: "qa",
    navLabel: "QA",
    name: "QA & Testing",
    short: "Manual, automated and API testing that catches problems before your users do.",
    long: "Manual, automated and API testing woven into development. We catch the bugs, regressions and edge cases before your customers do.",
    deliverables: ["Manual testing", "Automation", "API testing"],
    toolsLabel: "Tracked in",
    tools: ["Jira", "Confluence"],
  },
  {
    id: "marketing",
    navLabel: "Marketing",
    name: "Digital Marketing",
    short: "Paid, email and social campaigns measured against the numbers that matter to you.",
    long: "Launching is half the job. We plan and run paid, email and social campaigns, measure what works, and put more behind it.",
    deliverables: ["Strategy", "Paid ads", "Email", "Social", "Analytics"],
    toolsLabel: "Using",
    tools: ["Meta Ads", "Google Ads", "HubSpot", "Hootsuite", "Salesforce"],
  },
  {
    id: "support",
    navLabel: "Support",
    name: "Support & Maintenance",
    short: "Monitoring, fixes and improvements after launch, so your product keeps getting better.",
    long: "Software is never finished. We monitor, fix and improve your product after launch, so it stays fast, secure and current as your business changes.",
    deliverables: ["Bug fixes", "Performance", "Enhancements"],
    toolsLabel: "In touch via",
    tools: ["Slack", "Teams", "Email"],
  },
];

/** How a project runs — shown as a station track on the Services page. */
export const PROCESS = [
  { title: "Discover", text: "We learn your goals, users and constraints, and run the technical research." },
  { title: "Plan & scope", text: "A clear scope, architecture and roadmap agreed before build starts." },
  { title: "Design & build", text: "Design, engineering and QA move together, with regular demos along the way." },
  { title: "Launch & grow", text: "We ship, then stay on for support, marketing and what comes next." },
];
